import React, { useState, useMemo } from 'react';
import type { OverseasShipment, ShipmentItem, InventoryItem, Supplier } from '../../../types';
import XCircleIcon from '../../icons/XCircleIcon';
import CheckCircleIcon from '../../icons/CheckCircleIcon';
import InventoryUpdateDialog from './InventoryUpdateDialog';
import CreateInventoryItemDialog from './CreateInventoryItemDialog';

interface ReceiveShipmentModalProps {
  shipment: OverseasShipment;
  inventoryItems: InventoryItem[];
  suppliers: Supplier[];
  currentUserId: number;
  onClose: () => void;
  onMarkArrived: (shipmentId: string) => void;
  onUpdateReceivedQuantities: (shipmentId: string, items: ShipmentItem[]) => void;
  onCompleteReceiving: (shipmentId: string, inventoryUpdates: InventoryUpdate[]) => void;
  onCreateInventoryItem: (item: Omit<InventoryItem, 'id' | 'lastUpdated'>) => Promise<InventoryItem>;
  onUpdateInventoryItem: (itemId: number, updates: Partial<InventoryItem>) => Promise<void>;
}

interface InventoryUpdate {
  itemId: number;
  action: 'add' | 'adjust' | 'create';
  quantity: number;
  newItem?: Omit<InventoryItem, 'id' | 'lastUpdated'>;
}

type Phase = 'arrive' | 'check' | 'update' | 'review';

const ReceiveShipmentModal: React.FC<ReceiveShipmentModalProps> = ({
  shipment,
  inventoryItems,
  suppliers,
  currentUserId,
  onClose,
  onMarkArrived,
  onUpdateReceivedQuantities,
  onCompleteReceiving,
  onCreateInventoryItem,
  onUpdateInventoryItem,
}) => {
  const [phase, setPhase] = useState<Phase>(
    shipment.status === 'pending' ? 'arrive' :
    shipment.status === 'arrived' || shipment.status === 'receiving' ? 'check' : 'review'
  );
  const [receivedQuantities, setReceivedQuantities] = useState<{ [itemId: string]: number }>(() => {
    const initial: { [itemId: string]: number } = {};
    shipment.items?.forEach(item => {
      if (item.receivedQuantity !== undefined) {
        initial[item.id] = item.receivedQuantity;
      }
    });
    return initial;
  });
  const [itemNotes, setItemNotes] = useState<{ [itemId: string]: string }>({});
  const [itemStatuses, setItemStatuses] = useState<{ [itemId: string]: ShipmentItem['receivingStatus'] }>(() => {
    const initial: { [itemId: string]: ShipmentItem['receivingStatus'] } = {};
    shipment.items?.forEach(item => {
      initial[item.id] = item.receivingStatus || 'pending';
    });
    return initial;
  });
  const [inventoryUpdates, setInventoryUpdates] = useState<InventoryUpdate[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [currentProcessingItem, setCurrentProcessingItem] = useState<ShipmentItem | null>(null);

  // Find matching inventory item by name (fuzzy match)
  const findMatchingInventoryItem = (itemName: string): InventoryItem | null => {
    const lowerName = itemName.toLowerCase().trim();
    
    // Exact match
    let match = inventoryItems.find(item => item.name.toLowerCase().trim() === lowerName);
    if (match) return match;

    // Fuzzy match (contains)
    match = inventoryItems.find(item => 
      item.name.toLowerCase().includes(lowerName) || 
      lowerName.includes(item.name.toLowerCase())
    );
    if (match) return match;

    return null;
  };

  const handleMarkArrived = () => {
    onMarkArrived(shipment.id);
    setPhase('check');
  };

  const handleQuantityChange = (itemId: string, quantity: number) => {
    setReceivedQuantities(prev => ({
      ...prev,
      [itemId]: quantity,
    }));
    
    // Auto-update status
    const item = shipment.items?.find(i => i.id === itemId);
    if (item) {
      if (quantity === 0) {
        setItemStatuses(prev => ({ ...prev, [itemId]: 'missing' }));
      } else if (quantity < item.expectedQuantity) {
        setItemStatuses(prev => ({ ...prev, [itemId]: 'received' }));
      } else if (quantity === item.expectedQuantity) {
        setItemStatuses(prev => ({ ...prev, [itemId]: 'received' }));
      } else {
        setItemStatuses(prev => ({ ...prev, [itemId]: 'received' }));
      }
    }
  };

  const handleNoteChange = (itemId: string, note: string) => {
    setItemNotes(prev => ({
      ...prev,
      [itemId]: note,
    }));
  };

  const handleSavePhysicalStock = () => {
    // Update shipment items with received quantities
    const updatedItems = shipment.items?.map(item => ({
      ...item,
      receivedQuantity: receivedQuantities[item.id] ?? item.receivedQuantity,
      receivedUnit: item.expectedUnit,
      receivedDate: receivedQuantities[item.id] !== undefined ? Date.now() : item.receivedDate,
      receivingStatus: itemStatuses[item.id] || item.receivingStatus,
      notes: itemNotes[item.id] || item.notes,
    })) || [];

    onUpdateReceivedQuantities(shipment.id, updatedItems);
    setPhase('update');
    setCurrentItemIndex(0);
    processNextItem();
  };

  const processNextItem = () => {
    if (!shipment.items || currentItemIndex >= shipment.items.length) {
      setPhase('review');
      return;
    }

    const item = shipment.items[currentItemIndex];
    const receivedQty = receivedQuantities[item.id];
    
    // Skip if no quantity received
    if (!receivedQty || receivedQty === 0) {
      setCurrentItemIndex(prev => prev + 1);
      setTimeout(processNextItem, 100);
      return;
    }

    // Check if already processed
    const existingUpdate = inventoryUpdates.find(u => 
      u.itemId === item.matchedInventoryItemId || 
      (u.newItem && u.newItem.name === item.expectedName)
    );
    if (existingUpdate) {
      setCurrentItemIndex(prev => prev + 1);
      setTimeout(processNextItem, 100);
      return;
    }

    setCurrentProcessingItem(item);
    const match = findMatchingInventoryItem(item.expectedName);

    if (match) {
      // Item exists - show update dialog
      setShowUpdateDialog(true);
    } else {
      // Item doesn't exist - show create dialog
      setShowCreateDialog(true);
    }
  };

  const handleInventoryUpdate = (action: 'add' | 'adjust' | 'skip') => {
    if (!currentProcessingItem) return;

    const match = findMatchingInventoryItem(currentProcessingItem.expectedName);
    if (!match) return;

    const receivedQty = receivedQuantities[currentProcessingItem.id] || 0;

    if (action === 'add') {
      setInventoryUpdates(prev => [...prev, {
        itemId: match.id,
        action: 'add',
        quantity: receivedQty,
      }]);
    } else if (action === 'adjust') {
      setInventoryUpdates(prev => [...prev, {
        itemId: match.id,
        action: 'adjust',
        quantity: receivedQty,
      }]);
    }
    // Skip: do nothing

    setShowUpdateDialog(false);
    setCurrentProcessingItem(null);
    setCurrentItemIndex(prev => prev + 1);
    setTimeout(processNextItem, 100);
  };

  const handleCreateInventoryItem = async (newItem: Omit<InventoryItem, 'id' | 'lastUpdated'>) => {
    if (!currentProcessingItem) return;

    try {
      const created = await onCreateInventoryItem(newItem);
      setInventoryUpdates(prev => [...prev, {
        itemId: created.id,
        action: 'create',
        quantity: newItem.stock,
        newItem: newItem,
      }]);
    } catch (error) {
      console.error('Failed to create inventory item:', error);
      alert('Failed to create inventory item. Please try again.');
      return;
    }

    setShowCreateDialog(false);
    setCurrentProcessingItem(null);
    setCurrentItemIndex(prev => prev + 1);
    setTimeout(processNextItem, 100);
  };

  const handleSkipCreate = () => {
    setShowCreateDialog(false);
    setCurrentProcessingItem(null);
    setCurrentItemIndex(prev => prev + 1);
    setTimeout(processNextItem, 100);
  };

  const handleComplete = () => {
    onCompleteReceiving(shipment.id, inventoryUpdates);
    onClose();
  };

  const receivedItemsCount = Object.keys(receivedQuantities).filter(
    id => receivedQuantities[id] !== undefined && receivedQuantities[id] > 0
  ).length;

  const supplier = suppliers.find(s => s.id === shipment.supplierId);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Receive Shipment</h2>
              <p className="text-sm text-gray-600 mt-1">
                {shipment.shipmentNumber} - {supplier?.name || 'Unknown Supplier'}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Phase 1: Mark as Arrived */}
          {phase === 'arrive' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800">
                  This shipment is currently marked as <strong>Pending</strong>. 
                  Mark it as arrived to begin receiving physical stock.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkArrived}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Mark as Arrived
                </button>
              </div>
            </div>
          )}

          {/* Phase 2: Check Physical Stock */}
          {phase === 'check' && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800">
                  Enter the <strong>physical quantities actually received</strong> for each item.
                  This will be used to update your inventory.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serial #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expected</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate/Unit</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {shipment.items?.map((item) => {
                      const receivedQty = receivedQuantities[item.id] ?? item.receivedQuantity ?? '';
                      const status = itemStatuses[item.id] || item.receivingStatus || 'pending';
                      const note = itemNotes[item.id] || item.notes || '';
                      const variance = typeof receivedQty === 'number' 
                        ? receivedQty - item.expectedQuantity 
                        : null;
                      const varianceAmount = variance !== null && item.expectedCost
                        ? variance * item.expectedCost
                        : null;

                      return (
                        <tr key={item.id} className={status === 'missing' ? 'bg-red-50' : variance !== null && variance < 0 ? 'bg-yellow-50' : ''}>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900">{item.expectedSerialNumber || 'N/A'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">{item.expectedDescription || item.expectedName}</div>
                            {item.expectedSKU && (
                              <div className="text-xs text-gray-500">SKU: {item.expectedSKU}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900 font-medium">
                              {(() => {
                                // Extract only the numeric quantity, removing any dollar signs or cost values
                                const qty = typeof item.expectedQuantity === 'number' 
                                  ? item.expectedQuantity 
                                  : parseFloat(String(item.expectedQuantity).replace(/[^0-9.]/g, '')) || 0;
                                // Get unit, but filter out any dollar signs or cost values
                                const unit = item.expectedUnit && !item.expectedUnit.includes('$') 
                                  ? item.expectedUnit 
                                  : 'units';
                                return `${qty} ${unit}`;
                              })()}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={receivedQty === '' ? '' : receivedQty}
                              onChange={(e) => {
                                const val = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                                handleQuantityChange(item.id, val);
                              }}
                              className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                              placeholder="0"
                            />
                          </td>
                          <td className="px-4 py-3">
                            {variance !== null ? (
                              <div className={`text-sm font-semibold ${variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                {variance > 0 ? '+' : ''}{variance} {item.expectedUnit || 'units'}
                                {varianceAmount !== null && (
                                  <div className="text-xs text-gray-500">
                                    ${varianceAmount > 0 ? '+' : ''}{varianceAmount.toFixed(2)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900">
                              {item.expectedCost ? `$${item.expectedCost.toFixed(2)}` : 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900">
                              {item.expectedTotalAmount ? `$${item.expectedTotalAmount.toFixed(2)}` : 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={status}
                              onChange={(e) => setItemStatuses(prev => ({ ...prev, [item.id]: e.target.value as any }))}
                              className="text-sm border border-gray-300 rounded-md px-2 py-1"
                            >
                              <option value="pending">Pending</option>
                              <option value="received">Received</option>
                              <option value="missing">Missing</option>
                              <option value="damaged">Damaged</option>
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={note}
                              onChange={(e) => handleNoteChange(item.id, e.target.value)}
                              placeholder="Notes..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePhysicalStock}
                  className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
                >
                  Save & Continue to Inventory Update
                </button>
              </div>
            </div>
          )}

          {/* Phase 3: Update Inventory (processing items) */}
          {phase === 'update' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800">
                  Processing items... ({currentItemIndex + 1} / {shipment.items?.length || 0})
                </p>
              </div>
              <p className="text-gray-600">
                Please wait while we process each item...
              </p>
            </div>
          )}

          {/* Phase 4: Review */}
          {phase === 'review' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800">
                  Review the inventory changes before completing the receiving process.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800">Inventory Updates Summary</h3>
                
                {inventoryUpdates.length === 0 ? (
                  <p className="text-gray-500">No inventory updates to apply.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {inventoryUpdates.map((update, index) => {
                          const item = update.action === 'create' 
                            ? update.newItem
                            : inventoryItems.find(i => i.id === update.itemId);
                          
                          if (!item) return null;

                          return (
                            <tr key={index}>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  update.action === 'add' ? 'bg-green-100 text-green-800' :
                                  update.action === 'adjust' ? 'bg-blue-100 text-blue-800' :
                                  'bg-purple-100 text-purple-800'
                                }`}>
                                  {update.action === 'add' ? 'Add Stock' :
                                   update.action === 'adjust' ? 'Adjust Quantity' :
                                   'Create New'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">{item.name}</div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-gray-600">
                                  {update.action === 'add' && (
                                    <>Add {update.quantity} {item.unit} (Current: {item.stock}, New: {item.stock + update.quantity})</>
                                  )}
                                  {update.action === 'adjust' && (
                                    <>Set to {update.quantity} {item.unit}</>
                                  )}
                                  {update.action === 'create' && (
                                    <>Create with {update.quantity} {item.unit}</>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setPhase('check')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleComplete}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center"
                >
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  Complete Receiving
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Inventory Update Dialog */}
      {showUpdateDialog && currentProcessingItem && (
        <InventoryUpdateDialog
          itemName={currentProcessingItem.expectedName}
          currentStock={findMatchingInventoryItem(currentProcessingItem.expectedName)?.stock || 0}
          receivedQuantity={receivedQuantities[currentProcessingItem.id] || 0}
          unit={currentProcessingItem.expectedUnit || 'units'}
          onAddStock={() => handleInventoryUpdate('add')}
          onAdjustQuantity={() => handleInventoryUpdate('adjust')}
          onSkip={() => handleInventoryUpdate('skip')}
          onClose={() => {
            setShowUpdateDialog(false);
            setCurrentProcessingItem(null);
          }}
        />
      )}

      {/* Create Inventory Item Dialog */}
      {showCreateDialog && currentProcessingItem && (
        <CreateInventoryItemDialog
          itemName={currentProcessingItem.expectedName}
          receivedQuantity={receivedQuantities[currentProcessingItem.id] || 0}
          unit={currentProcessingItem.expectedUnit || 'units'}
          supplierName={supplier?.name}
          onCreate={handleCreateInventoryItem}
          onSkip={handleSkipCreate}
          onClose={() => {
            setShowCreateDialog(false);
            setCurrentProcessingItem(null);
          }}
        />
      )}
    </>
  );
};

export default ReceiveShipmentModal;
